import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { KTIcon } from '../../../helpers'
import { fetchSearch } from '../../../../app/api'
import { AnalyticsEvent, trackEvent } from '../../../../app/modules/apps/analytics'
import './Search.css'

const SEARCH_SEEN_KEY = 'myb-search-seen'
import {
  BankTransactionMeta,
  LedgerType,
  LedgerMeta,
  PaymentMeta,
  SearchResultItem,
  SearchResults,
  TransactionMeta,
  TxnType,
} from '../../../../app/types'

type EntityKey = 'contacts' | 'items' | 'transactions' | 'payments' | 'ledger' | 'bankTransactions'

const ENTITY_ORDER: EntityKey[] = ['contacts', 'items', 'transactions', 'payments', 'ledger', 'bankTransactions']

const ENTITY_LABELS: Record<EntityKey, string> = {
  contacts: 'Contacts',
  items: 'Items',
  transactions: 'Transactions',
  payments: 'Payments',
  ledger: 'Ledger / Journals',
  bankTransactions: 'Bank Transactions',
}

const ENTITY_ICONS: Record<EntityKey, string> = {
  contacts: 'profile-circle',
  items: 'abstract-26',
  transactions: 'document',
  payments: 'dollar',
  ledger: 'book',
  bankTransactions: 'bank',
}

const ENTITY_BADGE_CLASSES: Record<EntityKey, string> = {
  contacts: 'badge-light-primary',
  items: 'badge-light-success',
  transactions: 'badge-light-warning',
  payments: 'badge-light-info',
  ledger: 'badge-light-dark',
  bankTransactions: 'badge-light-danger',
}

const Search: FC = () => {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResults | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showDot, setShowDot] = useState(() => !localStorage.getItem(SEARCH_SEEN_KEY))

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const reset = useCallback(() => {
    setQuery('')
    setResults(null)
    setTotalCount(0)
    setError(null)
    setLoading(false)
    if (debounceRef.current) clearTimeout(debounceRef.current)
  }, [])

  const close = useCallback(() => {
    reset()
    setIsOpen(false)
  }, [reset])

  const handleOpen = () => {
    if (showDot) {
      localStorage.setItem(SEARCH_SEEN_KEY, '1')
      setShowDot(false)
    }
    setIsOpen(true)
    setTimeout(() => inputRef.current?.focus(), 50)
  }

  const handleQueryChange = (q: string) => {
    setQuery(q)
    setError(null)
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (q.length < 3) {
      setResults(null)
      setTotalCount(0)
      return
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const response = await fetchSearch(q)
        setResults(response.results)
        setTotalCount(response.totalCount)
        trackEvent(AnalyticsEvent.Search, { search_term: q, results_count: response.totalCount })
      } catch {
        setError('Search failed. Please try again.'.toTranslatedDisplay())
        setResults(null)
        setTotalCount(0)
      } finally {
        setLoading(false)
      }
    }, 300)
  }

  const buildNavigationPath = (item: SearchResultItem): string => {
    const { objectType, id, meta } = item
    switch (objectType) {
      case 'contact':
        return `/contacts/edit-contact/${id}`
      case 'item':
        return `/items/edit-item/${id}`
      case 'transaction': {
        const txnMeta = meta as unknown as TransactionMeta
        if (txnMeta.txnType === TxnType.Expense) return `/transactions/edit-expense/${id}`
        if (txnMeta.txnType === TxnType.StockAdjustment) return `/transactions/edit-stock-adjustment/${id}`
        return `/transactions/edit-txn/${txnMeta.txnType}/${id}`
      }
      case 'payment': {
        const payMeta = meta as unknown as PaymentMeta
        return `/transactions/edit-payment/${payMeta.paymentType}/${id}`
      }
      case 'ledger': {
        const ledgerMeta = meta as unknown as LedgerMeta
        if (ledgerMeta.ledgerType === LedgerType.BusinessTransaction)
          return `/transactions/edit-business-transaction/${id}`
        return `/transactions/edit-journal/${id}`
      }
      case 'bankTransaction': {
        const bankMeta = meta as unknown as BankTransactionMeta
        if (bankMeta.bankAcct) return `/banking/view-bank-transactions/${bankMeta.bankAcct}`
        return `/${item.navigateTo}`
      }
      default:
        return `/${item.navigateTo}`
    }
  }

  const handleResultClick = (item: SearchResultItem) => {
    navigate(buildNavigationPath(item))
    close()
  }

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen, close])

  // Escape to close
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, close])

  if (!isOpen) {
    return (
      <div className='d-flex align-items-stretch'>
        <div className='d-flex align-items-center'>
          <div
            className='btn btn-icon btn-custom btn-icon-muted btn-active-light btn-active-color-primary w-35px h-35px position-relative'
            onClick={handleOpen}
          >
            <KTIcon iconName='magnifier' className='fs-2' />
            {showDot && <span className='search-new-dot' />}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='d-flex align-items-stretch position-relative' ref={containerRef}>
      <div className='d-flex align-items-center'>
        {/* Expanded panel */}
        <div
          className='position-absolute bg-body rounded shadow card'
          style={{ top: '45px', right: 0, width: '375px', zIndex: 1050 }}
        >
          {/* Input row */}
          <div className='d-flex align-items-center px-4 py-3 border-bottom'>
            <KTIcon iconName='magnifier' className='fs-2 text-gray-500 me-2 flex-shrink-0' />
            <input
              ref={inputRef}
              type='text'
              className='form-control form-control-flush flex-grow-1'
              placeholder={'Search...'.toTranslatedDisplay()}
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              autoComplete='off'
            />
            {loading && (
              <span className='ms-2 flex-shrink-0'>
                <span className='spinner-border spinner-border-sm text-gray-500' />
              </span>
            )}
            <button
              className='btn btn-flush btn-active-color-primary ms-2 flex-shrink-0'
              onClick={close}
            >
              <KTIcon iconName='cross' className='fs-2' />
            </button>
          </div>

          {/* Body */}
          <div className='overflow-auto' style={{ maxHeight: '400px' }}>
            {/* Error */}
            {error && (
              <div className='px-4 py-3 text-danger fs-7'>{error}</div>
            )}

            {/* Hint */}
            {!error && !loading && query.length < 3 && (
              <div className='px-4 py-5 text-center text-muted fs-7'>
                {'Type at least 3 characters to search'.toTranslatedDisplay()}
              </div>
            )}

            {/* Empty state */}
            {!error && !loading && results !== null && totalCount === 0 && (
              <div className='text-center py-10'>
                <KTIcon iconName='search-list' className='fs-4x opacity-50' />
                <div className='mt-3'>
                  <div className='text-gray-600 fs-6 fw-bold'>{'No result found'.toTranslatedDisplay()}</div>
                  <div className='text-muted fs-7'>{'Please try again with a different query'.toTranslatedDisplay()}</div>
                </div>
              </div>
            )}

            {/* Results */}
            {!error && results !== null && totalCount > 0 &&
              ENTITY_ORDER.map((key) => {
                const items = results[key] as SearchResultItem[] | undefined
                if (!items || items.length === 0) return null
                const icon = ENTITY_ICONS[key]
                const badge = ENTITY_BADGE_CLASSES[key]
                const label = ENTITY_LABELS[key]
                return (
                  <div key={key}>
                    <div className='px-4 pt-4 pb-1'>
                      <span className='text-muted fs-7 fw-bold text-uppercase'>{label.toTranslatedDisplay()}</span>
                    </div>
                    {items.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => handleResultClick(item)}
                        className='d-flex align-items-center py-3 px-4 cursor-pointer bg-hover-light rounded'
                        style={{ cursor: 'pointer' }}
                      >
                        <div className='symbol symbol-35px me-3'>
                          <span className='symbol-label'>
                            <KTIcon iconName={icon} className='fs-3 text-gray-600' />
                          </span>
                        </div>
                        <div className='flex-grow-1 overflow-hidden'>
                          <div className='fw-bold text-gray-900 fs-6 text-truncate'>{item.displayText}</div>
                          <div className='text-muted fs-7 text-truncate'>{item.subText}</div>
                        </div>
                        <span className={`badge ${badge} ms-2 flex-shrink-0`}>{label.toTranslatedDisplay()}</span>
                      </div>
                    ))}
                  </div>
                )
              })
            }
          </div>
        </div>

        {/* Icon button (always visible when open) */}
        <div
          className='btn btn-icon btn-custom btn-icon-muted btn-active-light btn-active-color-primary w-35px h-35px'
          onClick={close}
        >
          <KTIcon iconName='magnifier' className='fs-2' />
        </div>
      </div>
    </div>
  )
}

export { Search }
